import React from 'react';
import styled from 'styled-components';
import { FaBuilding, FaRulerCombined, FaAward, FaUsers, FaRegLightbulb, FaPhoneAlt, FaMapMarkerAlt, FaClock, FaEnvelope } from 'react-icons/fa';
import DemoViewer from '../../components/DemoViewer';
import SEO from '../../components/SEO';

// Estilos para la demo de arquitectos
const ArquitectosDemo = styled.div`
  font-family: 'Montserrat', sans-serif;
  color: #333;
`;

const HeroSection = styled.div`
  background-image: url('/servicios/tipo-cliente/arquitectos-hero.webp');
  background-size: cover;
  background-position: center;
  height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 0 4rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 0 2rem;
  }
`;

const StudioName = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #fff;
  margin-bottom: 1rem;
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 2rem;
  color: white;
  max-width: 700px;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin-bottom: 3rem;
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const PrimaryButton = styled.button`
  background-color: #d5a353;
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #c69345;
    transform: translateY(-3px);
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: white;
  border: 1px solid white;
  padding: 1rem 2.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-3px);
  }
`;

const Section = styled.section`
  padding: 6rem 2rem;
`;

const LightSection = styled(Section)`
  background-color: white;
`;

const DarkSection = styled(Section)`
  background-color: #101010;
  color: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: #d5a353;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #777;
  text-align: center;
  max-width: 800px;
  margin: -2rem auto 3rem;
  line-height: 1.6;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: white;
  padding: 2.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  color: #d5a353;
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #333;
`;

const ServiceDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
  margin-bottom: 1.5rem;
`;

const ServiceLink = styled.a`
  display: inline-block;
  color: #d5a353;
  font-weight: 500;
  text-decoration: none;
  position: relative;
  
  &::after {
    content: '→';
    margin-left: 0.5rem;
    transition: margin-left 0.3s ease;
  }
  
  &:hover::after {
    margin-left: 0.8rem;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  position: relative;
  overflow: hidden;
  height: 400px;
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &:hover .overlay {
    opacity: 1;
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const ProjectOverlay = styled.div.attrs({ className: 'overlay' })`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const ProjectTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const ProjectCategory = styled.p`
  color: #d5a353;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ProjectButton = styled.button`
  background-color: transparent;
  color: white;
  border: 1px solid white;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: white;
    color: #333;
  }
`;

const ProcessSection = styled(Section)`
  background-color: #f9f9f9;
`;

const ProcessSteps = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 2rem;
  margin-top: 4rem;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const ProcessStep = styled.div`
  flex: 1;
  min-width: 250px;
  text-align: center;
  padding: 0 1rem;
`;

const StepNumber = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #d5a353;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 auto 2rem;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const StepDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const TeamSection = styled(LightSection)``;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2.5rem;
`;

const TeamMember = styled.div`
  text-align: center;
`;

const MemberImage = styled.div`
  width: 100%;
  height: 300px;
  background-size: cover;
  background-position: center;
  margin-bottom: 1.5rem;
`;

const MemberName = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
`;

const MemberRole = styled.p`
  color: #d5a353;
  margin-bottom: 1rem;
  font-style: italic;
`;

const MemberBio = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const ContactSection = styled(DarkSection)``;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.div`
  text-align: center;
  padding: 2rem;
  background-color: rgba(255, 255, 255, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ContactIcon = styled.div`
  font-size: 2.5rem;
  color: #d5a353;
  margin-bottom: 1.5rem;
`;

const ContactTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: white;
`;

const ContactInfo = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
`;

const ContactForm = styled.form`
  margin-top: 5rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: none;
  color: white;
  font-size: 1rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: 1px solid #d5a353;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: none;
  color: white;
  font-size: 1rem;
  resize: vertical;
  min-height: 150px;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: 1px solid #d5a353;
  }
`;

const SubmitButton = styled.button`
  background-color: #d5a353;
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  grid-column: 1 / -1;
  width: 200px;
  justify-self: end;
  
  &:hover {
    background-color: #c69345;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Footer = styled.footer`
  background-color: #070707;
  padding: 2rem;
  text-align: center;
  
  p {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.9rem;
  }
`;

const ArquitectosDemoPage: React.FC = () => {
  return (
    <>
      <SEO 
        title="Demo de Sitio Web para Arquitectos | Web a Bajo Coste"
        description="Visualiza cómo podría ser tu página web de arquitectura con este ejemplo interactivo. Diseño elegante para mostrar tus proyectos y servicios profesionales."
        keywords="demo web arquitectos, diseño web arquitectura, ejemplo web estudio arquitectura, página web arquitectos"
        canonical="https://webabajocoste.es/plantillas/arquitectos"
      >
        <meta name="robots" content="noindex, nofollow" />
      </SEO>
      
      <DemoViewer
        title="Demo Web para Arquitectos"
        subtitle="Visualiza cómo podría ser tu nueva web de arquitectura, diseñada para destacar tus proyectos y servicios de forma elegante y profesional"
        domainName="https://archiline-studio.es"
        serviceLink="/servicios/diseno-web-arquitectos"
        serviceName="Arquitectos"
      >
        <ArquitectosDemo>
          <HeroSection>
            <HeroContent>
              <StudioName>Archiline Studio</StudioName>
              <HeroTitle>Diseñamos espacios que inspiran vida</HeroTitle>
              <HeroSubtitle>Estudio de arquitectura especializado en proyectos residenciales, comerciales y sostenibles con un enfoque innovador y funcional.</HeroSubtitle>
              
              <HeroButtons>
                <PrimaryButton>Nuestros Proyectos</PrimaryButton>
                <SecondaryButton>Contacta con nosotros</SecondaryButton>
              </HeroButtons>
            </HeroContent>
          </HeroSection>
          
          <LightSection>
            <Container>
              <SectionTitle>Nuestros Servicios</SectionTitle>
              <SectionSubtitle>Ofrecemos soluciones arquitectónicas integrales adaptadas a las necesidades de cada cliente</SectionSubtitle>
              
              <ServicesGrid>
                <ServiceCard>
                  <ServiceIcon>
                    <FaBuilding />
                  </ServiceIcon>
                  <ServiceTitle>Arquitectura Residencial</ServiceTitle>
                  <ServiceDescription>
                    Diseñamos viviendas a medida que se adaptan a tu estilo de vida y necesidades, desde la conceptualización hasta la finalización del proyecto.
                  </ServiceDescription>
                  <ServiceLink href="#">Descubrir más</ServiceLink>
                </ServiceCard>
                
                <ServiceCard>
                  <ServiceIcon>
                    <FaRulerCombined />
                  </ServiceIcon>
                  <ServiceTitle>Diseño de Interiores</ServiceTitle>
                  <ServiceDescription>
                    Creamos espacios internos funcionales y estéticamente atractivos que reflejan la personalidad y estilo de cada cliente.
                  </ServiceDescription>
                  <ServiceLink href="#">Descubrir más</ServiceLink>
                </ServiceCard>
                
                <ServiceCard>
                  <ServiceIcon>
                    <FaRegLightbulb />
                  </ServiceIcon>
                  <ServiceTitle>Arquitectura Sostenible</ServiceTitle>
                  <ServiceDescription>
                    Desarrollamos proyectos respetuosos con el medio ambiente, implementando soluciones energéticamente eficientes y materiales sostenibles.
                  </ServiceDescription>
                  <ServiceLink href="#">Descubrir más</ServiceLink>
                </ServiceCard>
              </ServicesGrid>
            </Container>
          </LightSection>
          
          <DarkSection>
            <Container>
              <SectionTitle>Proyectos Destacados</SectionTitle>
              
              <ProjectsGrid>
                <ProjectCard>
                  <ProjectImage src="/servicios/tipo-cliente/arquitectos-proyecto1.webp" alt="Casa Luminosa" />
                  <ProjectOverlay>
                    <ProjectTitle>Casa Luminosa</ProjectTitle>
                    <ProjectCategory>Residencial</ProjectCategory>
                    <ProjectButton>Ver Proyecto</ProjectButton>
                  </ProjectOverlay>
                </ProjectCard>
                
                <ProjectCard>
                  <ProjectImage src="/servicios/tipo-cliente/arquitectos-proyecto2.webp" alt="Oficinas Modernas" />
                  <ProjectOverlay>
                    <ProjectTitle>Oficinas Modernas</ProjectTitle>
                    <ProjectCategory>Comercial</ProjectCategory>
                    <ProjectButton>Ver Proyecto</ProjectButton>
                  </ProjectOverlay>
                </ProjectCard>
                
                <ProjectCard>
                  <ProjectImage src="/servicios/tipo-cliente/arquitectos-proyecto3.webp" alt="Espacio Cultural" />
                  <ProjectOverlay>
                    <ProjectTitle>Espacio Cultural</ProjectTitle>
                    <ProjectCategory>Público</ProjectCategory>
                    <ProjectButton>Ver Proyecto</ProjectButton>
                  </ProjectOverlay>
                </ProjectCard>
                
                <ProjectCard>
                  <ProjectImage src="/servicios/tipo-cliente/arquitectos-proyecto4.webp" alt="Vivienda Sostenible" />
                  <ProjectOverlay>
                    <ProjectTitle>Vivienda Sostenible</ProjectTitle>
                    <ProjectCategory>Residencial Ecológico</ProjectCategory>
                    <ProjectButton>Ver Proyecto</ProjectButton>
                  </ProjectOverlay>
                </ProjectCard>
                
                <ProjectCard>
                  <ProjectImage src="/servicios/tipo-cliente/arquitectos-proyecto5.webp" alt="Restaurante Céntrico" />
                  <ProjectOverlay>
                    <ProjectTitle>Restaurante Céntrico</ProjectTitle>
                    <ProjectCategory>Hostelería</ProjectCategory>
                    <ProjectButton>Ver Proyecto</ProjectButton>
                  </ProjectOverlay>
                </ProjectCard>
                
                <ProjectCard>
                  <ProjectImage src="/servicios/tipo-cliente/arquitectos-proyecto6.webp" alt="Casa del Lago" />
                  <ProjectOverlay>
                    <ProjectTitle>Casa del Lago</ProjectTitle>
                    <ProjectCategory>Residencial</ProjectCategory>
                    <ProjectButton>Ver Proyecto</ProjectButton>
                  </ProjectOverlay>
                </ProjectCard>
              </ProjectsGrid>
            </Container>
          </DarkSection>
          
          <ProcessSection>
            <Container>
              <SectionTitle>Nuestro Proceso</SectionTitle>
              <SectionSubtitle>Trabajamos de manera metódica y personalizada en cada proyecto, siguiendo un proceso estructurado que garantiza los mejores resultados</SectionSubtitle>
              
              <ProcessSteps>
                <ProcessStep>
                  <StepNumber>1</StepNumber>
                  <StepTitle>Consulta Inicial</StepTitle>
                  <StepDescription>
                    Comenzamos con una reunión detallada para entender tus necesidades, visión y expectativas para el proyecto.
                  </StepDescription>
                </ProcessStep>
                
                <ProcessStep>
                  <StepNumber>2</StepNumber>
                  <StepTitle>Conceptualización</StepTitle>
                  <StepDescription>
                    Desarrollamos conceptos preliminares y bocetos basados en tus requisitos, considerando aspectos funcionales y estéticos.
                  </StepDescription>
                </ProcessStep>
                
                <ProcessStep>
                  <StepNumber>3</StepNumber>
                  <StepTitle>Diseño Detallado</StepTitle>
                  <StepDescription>
                    Refinamos el concepto elegido, creando planos detallados, renders 3D y especificaciones técnicas del proyecto.
                  </StepDescription>
                </ProcessStep>
                
                <ProcessStep>
                  <StepNumber>4</StepNumber>
                  <StepTitle>Implementación</StepTitle>
                  <StepDescription>
                    Supervisamos la ejecución del proyecto, garantizando que se cumplan los estándares de calidad y los plazos establecidos.
                  </StepDescription>
                </ProcessStep>
              </ProcessSteps>
            </Container>
          </ProcessSection>
          
          <TeamSection>
            <Container>
              <SectionTitle>Nuestro Equipo</SectionTitle>
              <SectionSubtitle>Contamos con profesionales experimentados y creativos, apasionados por la arquitectura y el diseño</SectionSubtitle>
              
              <TeamGrid>
                <TeamMember>
                  <MemberImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/arquitecto1.webp")' }} />
                  <MemberName>Carlos Mendoza</MemberName>
                  <MemberRole>Arquitecto Principal</MemberRole>
                  <MemberBio>
                    Con más de 15 años de experiencia en proyectos residenciales y comerciales a nivel nacional e internacional.
                  </MemberBio>
                </TeamMember>
                
                <TeamMember>
                  <MemberImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/arquitecto2.webp")' }} />
                  <MemberName>Elena Sánchez</MemberName>
                  <MemberRole>Diseñadora de Interiores</MemberRole>
                  <MemberBio>
                    Especialista en diseño de interiores con un enfoque en espacios funcionales y estéticamente armoniosos.
                  </MemberBio>
                </TeamMember>
                
                <TeamMember>
                  <MemberImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/arquitecto3.webp")' }} />
                  <MemberName>Javier Rodríguez</MemberName>
                  <MemberRole>Arquitecto Técnico</MemberRole>
                  <MemberBio>
                    Experto en supervisión de obras y cumplimiento de normativas técnicas y urbanísticas.
                  </MemberBio>
                </TeamMember>
                
                <TeamMember>
                  <MemberImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/arquitecto4.webp")' }} />
                  <MemberName>Lucía Torres</MemberName>
                  <MemberRole>Especialista en Sostenibilidad</MemberRole>
                  <MemberBio>
                    Dedicada a implementar soluciones eco-amigables y eficientes energéticamente en todos nuestros proyectos.
                  </MemberBio>
                </TeamMember>
              </TeamGrid>
            </Container>
          </TeamSection>
          
          <ContactSection>
            <Container>
              <SectionTitle>Contacto</SectionTitle>
              <SectionSubtitle>Estamos listos para hacer realidad tu próximo proyecto. Contáctanos para comenzar una conversación</SectionSubtitle>
              
              <ContactGrid>
                <ContactCard>
                  <ContactIcon>
                    <FaMapMarkerAlt />
                  </ContactIcon>
                  <ContactTitle>Ubicación</ContactTitle>
                  <ContactInfo>
                    Calle Gran Vía 41, 4ºB<br />
                    28013 Madrid
                  </ContactInfo>
                </ContactCard>
                
                <ContactCard>
                  <ContactIcon>
                    <FaPhoneAlt />
                  </ContactIcon>
                  <ContactTitle>Teléfono</ContactTitle>
                  <ContactInfo>
                    +34 912 345 678<br />
                    +34 645 123 456
                  </ContactInfo>
                </ContactCard>
                
                <ContactCard>
                  <ContactIcon>
                    <FaEnvelope />
                  </ContactIcon>
                  <ContactTitle>Email</ContactTitle>
                  <ContactInfo>
                    info@archiline-studio.es<br />
                    proyectos@archiline-studio.es
                  </ContactInfo>
                </ContactCard>
              </ContactGrid>
              
              <ContactForm>
                <FormGroup>
                  <FormInput type="text" placeholder="Nombre" />
                </FormGroup>
                
                <FormGroup>
                  <FormInput type="email" placeholder="Email" />
                </FormGroup>
                
                <FormGroup>
                  <FormInput type="text" placeholder="Teléfono" />
                </FormGroup>
                
                <FormGroup>
                  <FormInput type="text" placeholder="Tipo de proyecto" />
                </FormGroup>
                
                <FormGroup className="full-width">
                  <FormTextarea placeholder="Descríbenos tu proyecto" />
                </FormGroup>
                
                <SubmitButton>Enviar Mensaje</SubmitButton>
              </ContactForm>
            </Container>
          </ContactSection>
          
          <Footer>
            <p>© 2023 Archiline Studio | Todos los derechos reservados</p>
          </Footer>
        </ArquitectosDemo>
      </DemoViewer>
    </>
  );
};

export default ArquitectosDemoPage; 